import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useTheme } from "@mui/material/styles";

import { useCreatePost } from "../../../hooks/blog/posts/useCreatePost";
import { useUpdatePost } from "../../../hooks/blog/posts/useUpdatePost";
import { useGetPostCategories } from "../../../hooks/blog/categories/useGetPostCategories";
import { useGetPostTags } from "../../../hooks/blog/tags/useGetPostTags";

import FormInput from "../../inputs/FormInput";
import LanguageToggle from "../../global/LanguageToggle";
import { slugify } from "../../../utils/slugify";

const FormAddEditPost = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Fields
    const [titleAr, setTitleAr] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [slug, setSlug] = useState("");
    const [contentAr, setContentAr] = useState("");
    const [contentEn, setContentEn] = useState("");
    const [excerptAr, setExcerptAr] = useState("");
    const [excerptEn, setExcerptEn] = useState("");
    const [categoryId, setCategoryId] = useState("");
    console.log("categoryId", categoryId);
    const [tagIds, setTagIds] = useState([]);
    const [isPublished, setIsPublished] = useState(false);
    const [isFeatured, setIsFeatured] = useState(false);
    const [imagePreview, setImagePreview] = useState("");

    // Handle image file from InputFileUpload
    const handleImageChange = (e) => {
        const fileName = e.target.value;
        setImagePreview(fileName);
        // For FormData, we'll need the actual file object
        // This will be handled differently since InputFileUpload uploads automatically
    };

    // Mutations
    const { createPostMutate, isCreating } = useCreatePost();
    const { updatePostMutate, isUpdating } = useUpdatePost();

    // Data for Selects
    const { categories } = useGetPostCategories({ per_page: 100 });
    const { tags } = useGetPostTags({ per_page: 100 });
    const categoryOptions = categories.map(c => ({ id: c.id, name: { ar: c.name?.ar, en: c.name?.en } }));
    const tagOptions = tags.map(t => ({ id: t.id, name: { ar: t.name?.ar, en: t.name?.en } }));


    useEffect(() => {
        if (isEdit && currentData) {
            setTitleAr(currentData.title?.ar || "");
            setTitleEn(currentData.title?.en || "");
            setSlug(currentData.slug || "");
            setContentAr(currentData.content?.ar || "");
            setContentEn(currentData.content?.en || "");
            setExcerptAr(currentData.excerpt?.ar || "");
            setExcerptEn(currentData.excerpt?.en || "");
            setCategoryId(currentData.category?.id || "");
            setTagIds(
                Array.isArray(currentData.tags)
                    ? currentData.tags.map(tag =>
                        typeof tag === 'object' ? Number(tag.id) : Number(tag)
                    )
                    : []
            );

            setIsPublished(currentData.is_published || false);
            setIsFeatured(currentData.is_featured || false);
            setImagePreview(currentData.featured_image || "");
        }
    }, [isEdit, currentData]);

    // Auto-generate slug
    useEffect(() => {
        if (titleEn) {
            setSlug(slugify(titleEn));
        }
    }, [titleEn]);

    const handleTagsChange = (event) => {
        const {
            target: { value },
        } = event;

        const valuesArray =
            typeof value === 'string' ? value.split(',') : value;

        setTagIds(valuesArray.map(Number));
    };


    const SubmitForm = (e) => {
        e.preventDefault();

        // Use FormData for file upload
        const formData = new FormData();
        formData.append("title[en]", titleEn || titleAr);
        formData.append("title[ar]", titleAr || titleEn);
        formData.append("slug", slug);
        formData.append("content[en]", contentEn);
        formData.append("content[ar]", contentAr);
        formData.append("excerpt[en]", excerptEn);
        formData.append("excerpt[ar]", excerptAr);
        formData.append("category_id", categoryId);
        tagIds.forEach((id) => {
            formData.append("tag_ids[]", id);
        });

        formData.append("is_published", isPublished ? 1 : 0);
        formData.append("is_featured", isFeatured ? 1 : 0);

        if (imagePreview) {
            formData.append("featured_image", imagePreview);
        }

        if (isEdit) {
            updatePostMutate({ id: currentData.id, data: formData });
        } else {
            createPostMutate(formData);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 4 },
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#FFFFFF",
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Box component="form" onSubmit={SubmitForm} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Box>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                            {isEdit
                                ? (isArabic ? "تعديل المقال" : "Edit Post")
                                : (isArabic ? "إضافة مقال جديد" : "Add New Post")}
                        </Typography>
                    </Box>
                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={4}>
                    {/* Basic Info */}
                    <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold">
                            {isArabic ? "البيانات الأساسية" : "Basic Information"}
                        </Typography>
                        <Divider sx={{ mb: 3, mt: 1 }} />
                    </Grid>

                    {/* Title */}
                    <Grid item xs={12} md={6}>
                        {activeLang === "ar" ? (
                            <FormInput
                                label="العنوان (عربي)"
                                placeholder="عنوان المقال"
                                type="text"
                                value={titleAr}
                                onChange={(e) => setTitleAr(e.target.value)}
                                required
                            />
                        ) : (
                            <FormInput
                                label="Title (English)"
                                placeholder="Post Title"
                                type="text"
                                value={titleEn}
                                onChange={(e) => setTitleEn(e.target.value)}
                                required
                            />
                        )}
                    </Grid>

                    {/* Slug */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الرابط (Slug)" : "Slug"}
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            required
                            disabled={true}
                        />
                    </Grid>

                    {/* Category */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "التصنيف" : "Category"}
                            type="select"
                            name="category_id"
                            value={categoryId}
                            onChange={(e) => setCategoryId(e.target.value)}
                            options={categoryOptions}
                            required
                        />
                    </Grid>

                    {/* Tags */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الوسوم" : "Tags"}
                            type="multi_select"
                            name="tag_ids"
                            selectedValue={tagIds}
                            handleSelectChange={handleTagsChange}
                            options={tagOptions}
                        />
                    </Grid>

                    {/* Publishing Options */}
                    <Grid item xs={12} md={4}>
                        <FormInput
                            label={isArabic ? "منشور" : "Published"}
                            type="switch"
                            name="is_published"
                            checked={isPublished}
                            onChange={(e) => setIsPublished(e.target.checked)}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput
                            label={isArabic ? "مميز" : "Featured"}
                            type="switch"
                            name="is_featured"
                            checked={isFeatured}
                            onChange={(e) => setIsFeatured(e.target.checked)}
                        />
                    </Grid>


                    {/* Image Upload */}
                    <Grid item xs={12}>
                        <FormInput
                            label={isArabic ? "الصورة البارزة" : "Featured Image"}
                            type="file"
                            name="featured_image"
                            value={imagePreview}
                            onChange={handleImageChange}
                            width={200}
                            height={150}
                            borderRadius="8px"
                        />
                    </Grid>

                    {/* Content Editor */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                            {activeLang === "ar" ? "المحتوى (عربي)" : "Content (English)"}
                        </Typography>
                        {activeLang === "ar" ? (
                            <FormInput
                                type="editor"
                                value={contentAr}
                                onChange={(e) => setContentAr(e.target.value)}
                                placeholder="اكتب المحتوى هنا..."
                                name="content_ar"
                                textareaRows="250px"
                            />
                        ) : (
                            <FormInput
                                type="editor"
                                value={contentEn}
                                onChange={(e) => setContentEn(e.target.value)}
                                placeholder="Write your content here..."
                                name="content_en"
                                textareaRows="250px"
                            />
                        )}
                    </Grid>

                    {/* Excerpt */}
                    <Grid item xs={12}>
                        {activeLang === "ar" ? (
                            <FormInput
                                label="مقتطف (عربي)"
                                placeholder="وصف قصير للمقال"
                                type="text"
                                value={excerptAr}
                                onChange={(e) => setExcerptAr(e.target.value)}
                                multiline
                                rows={2}
                            />
                        ) : (
                            <FormInput
                                label="Excerpt (English)"
                                placeholder="Short description of the post"
                                type="text"
                                value={excerptEn}
                                onChange={(e) => setExcerptEn(e.target.value)}
                                multiline
                                rows={2}
                            />
                        )}
                    </Grid>

                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" justifyContent="flex-end">
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={isCreating || isUpdating}
                        sx={{
                            minWidth: 160,
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            boxShadow: theme.shadows[4],
                        }}
                    >
                        {isCreating || isUpdating ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "نشر" : "Publish"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditPost;
